import { useState } from 'react';
import { FileText, User, Calendar, MapPin, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CouponStatusBadge } from '../shared/StatusBadge';
import { FareInfoCard, OriginalDocumentCard } from './FareInfoCard';
import { getTravelerName, formatMoney } from '../utils/formatters';
import { getPaymentMethodName } from '../utils/statusCodes';
import type { TravelDocument, Traveler } from '../utils/types';

interface TicketCardProps {
  ticket: TravelDocument;
  travelers: Traveler[];
  className?: string;
}

export function TicketCard({ ticket, travelers, className }: TicketCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const travelerName = ticket.travelerIds[0]
    ? getTravelerName(travelers, ticket.travelerIds[0])
    : 'Unknown';

  const issueDate = ticket.creation?.localDateTime
    ? new Date(ticket.creation.localDateTime).toLocaleDateString('de-DE')
    : null;

  const officeId = ticket.creation?.office?.officeId;

  // Get unique coupon statuses
  const couponStatuses = ticket.fareInfos
    ?.map(f => f.couponStatus)
    .filter((v, i, arr) => arr.indexOf(v) === i) || [];

  // Get payment method
  const paymentMethod = ticket.paymentTransactions?.[0]?.paymentMethod;
  const paymentName = paymentMethod?.vendorCode
    ? getPaymentMethodName(paymentMethod.vendorCode)
    : null;

  // Get unique fare family codes to display
  const fareFamilies = ticket.fareInfos
    ?.map(f => f.fareFamilyCode)
    .filter((v, i, arr) => v && arr.indexOf(v) === i) || [];
  const primaryFareFamily = fareFamilies[0];

  return (
    <div
      className={cn(
        'rounded-lg border border-slate-200 bg-white shadow-sm',
        'transition-all duration-200',
        isExpanded ? 'shadow-md' : 'hover:shadow-md',
        className
      )}
    >
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 text-left"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <FileText className="h-4 w-4 text-slate-500" />
            </div>
            <div>
              <div className="font-mono text-lg font-semibold text-slate-900">
                {ticket.id}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <User className="h-3 w-3" />
                <span>{travelerName}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Fare family badge */}
            {primaryFareFamily && (
              <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                {primaryFareFamily}
              </span>
            )}

            {/* Coupon statuses */}
            <div className="flex items-center gap-1">
              {couponStatuses.map((status, idx) => (
                <CouponStatusBadge key={idx} status={status} />
              ))}
            </div>

            {/* Expand indicator */}
            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-slate-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-500" />
              )}
            </div>
          </div>
        </div>

        {/* Issue info row */}
        <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
          {issueDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>Issued {issueDate}</span>
            </div>
          )}
          {officeId && (
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              <span className="font-mono">{officeId}</span>
            </div>
          )}
          {paymentName && (
            <div className="flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              <span>{paymentName}</span>
            </div>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {/* Fare info cards */}
          {ticket.fareInfos && ticket.fareInfos.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Coupons
              </div>
              {ticket.fareInfos.map((fareInfo, idx) => (
                <FareInfoCard key={idx} fareInfo={fareInfo} />
              ))}
            </div>
          )}

          {/* Original documents */}
          {ticket.originalDocuments && ticket.originalDocuments.length > 0 && (
            <div className="space-y-2 mt-4">
              <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Original Documents
              </div>
              {ticket.originalDocuments.map((doc, idx) => (
                <OriginalDocumentCard
                  key={idx}
                  documentNumber={doc.documentNumber}
                  issueDate={doc.creation.dateTime}
                />
              ))}
            </div>
          )}

          {/* Price info if available */}
          {ticket.price && ticket.price.total > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Total</span>
                <span className="font-semibold text-slate-900">
                  {formatMoney(ticket.price.total, ticket.price.currencyCode)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
